import type { Event } from '@bugsnag/js';

/**
 * Axios codes for requests that never got a response: the client went offline,
 * timed out, or the request was aborted by navigation/cancellation. A response
 * with a 4xx/5xx status has a different code and is still reported.
 */
const CLIENT_SIDE_AXIOS_CODES = ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ERR_CANCELED'];

/**
 * Error classes for requests that never got a response: a fetch/XHR aborted by
 * the browser, and Inertia's HTTP client failing on a lost connection or a
 * cancelled visit. A response with a 4xx/5xx status is `HttpResponseError` and
 * is still reported.
 */
const CLIENT_SIDE_ERROR_CLASSES = ['AbortError', 'HttpNetworkError', 'HttpCancelledError'];

/**
 * Benign browser warning raised when observed elements resize again within the
 * same frame ("loop limit exceeded" / "loop completed with undelivered
 * notifications"). It carries no stack and is not actionable.
 */
const RESIZE_OBSERVER_LOOP_PREFIX = 'ResizeObserver loop';

const isClientSideAxiosFailure = (error: unknown): boolean => {
    const axiosError = error as { isAxiosError?: boolean; code?: string } | null;

    return (
        axiosError?.isAxiosError === true &&
        CLIENT_SIDE_AXIOS_CODES.includes(axiosError.code ?? '')
    );
};

/**
 * True when the stack has frames but none of them come from the page itself or
 * our asset host, i.e. the error was thrown by a third-party script, a browser
 * extension or injected code. Events without any frames are kept.
 */
const isForeignStack = (event: Event, ownOrigins: string[]): boolean => {
    const frames = event.errors[0]?.stacktrace ?? [];

    if (frames.length === 0) {
        return false;
    }

    return !frames.some((frame) =>
        ownOrigins.some((origin) => (frame.file ?? '').startsWith(origin))
    );
};

/**
 * Bugsnag `onError` filter that drops noise which is not a bug in our code.
 *
 * @returns false to discard the event.
 */
export const shouldReportBugsnagEvent = (event: Event, ownOrigins: string[]): boolean => {
    if (isClientSideAxiosFailure(event.originalError)) {
        return false;
    }

    if (CLIENT_SIDE_ERROR_CLASSES.includes(event.errors[0]?.errorClass ?? '')) {
        return false;
    }

    if ((event.errors[0]?.errorMessage ?? '').startsWith(RESIZE_OBSERVER_LOOP_PREFIX)) {
        return false;
    }

    return !isForeignStack(event, ownOrigins.filter(Boolean));
};
